import { toTelHref } from "@/utils/string.util";

/**
 * Copies the current page query onto a destination link.
 * Keys already on the destination stay as authored. Excluded keys are dropped.
 */
export function preservePageSearchParams(
  href: string,
  pageSearch: Pick<URLSearchParams, "forEach">,
  excludedKeys: readonly string[] = [],
): string {
  const excluded = new Set(excludedKeys);
  const isRelative = href.startsWith("/");
  const destination = new URL(href, "http://local.invalid");

  pageSearch.forEach((value, key) => {
    if (excluded.has(key) || destination.searchParams.has(key)) return;
    destination.searchParams.append(key, value);
  });

  if (!isRelative) return destination.toString();

  return `${destination.pathname}${destination.search}${destination.hash}`;
}
export function appendQueryString(
  url: string | null | undefined,
  queryString: string | null | undefined,
): string | null {
  if (!url) return null;
  const trimmed = queryString?.trim();
  if (!trimmed) return url;
  const q = trimmed.replace(/^\?/, "");
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}${q}`;
}

export function resolveLinkHref(
  link: LinkHrefInput | null | undefined,
): string {
  if (!link) return "#";
  if (link.linkType === "anchor" && link.anchorId) {
    return `#${link.anchorId}`;
  }
  return appendQueryString(link.url, link.queryString) ?? "#";
}

export function resolveCtaLink(fields: CtaLinkFields): ResolvedCtaLink | null {
  if (fields.ctaType === "phone") {
    const label = fields.phoneNumber?.trim();
    const href = toTelHref(fields.phoneNumber);
    if (!label || !href) return null;
    return { href, label, phone: true, openInNewTab: false };
  }

  const label = fields.ctaLabel?.trim();
  const href = resolveLinkHref(fields.ctaLink);
  if (!label || !href) return null;

  return {
    href,
    label,
    phone: false,
    openInNewTab: fields.ctaLink?.openInNewTab ?? false,
  };
}

type LinkHrefInput = {
  url?: string | null;
  queryString?: string | null;
  linkType?: string | null;
  anchorId?: string | null;
  openInNewTab?: boolean | null;
};

export type CtaLinkFields = {
  ctaType?: string | null;
  ctaLabel?: string | null;
  phoneNumber?: string | null;
  ctaLink?: LinkHrefInput | null;
};

export type ResolvedCtaLink = {
  href: string;
  label: string;
  phone: boolean;
  openInNewTab: boolean;
};
